/**
 * <g-chart> - Chart.js wrapper supporting all Chart.js variants
 *
 * Self-aggregating chart. Pass `.records` + `.config` and the component
 * groups/aggregates and renders. Owns canvas creation, Chart.getChart
 * cleanup on data refresh, and color palette mapping. The host's Alpine
 * code never touches Chart.js directly.
 *
 * Supported `type` values:
 *   - bar             single-series categorical (default)
 *   - horizontalBar   bar with indexAxis: 'y'
 *   - line            single-series line
 *   - area            line with fill: true
 *   - pie / doughnut  proportional, one slice per group
 *   - polarArea       like pie but variable radius per slice
 *   - radar           multi-axis polygon, one per group
 *   - bubble          XY points with radius — needs xField/yField/rField
 *   - scatter         XY points — needs xField/yField
 *   - mixed           multiple datasets, each with its own type config
 *
 * @example  Simple categorical:
 *   c.config = { type: 'bar', groupBy: 'Assigned To',
 *                metric: 'sum', field: 'Remaining Balance', palette: 'primary' };
 *
 * @example  Per-segment colors (best for status fields):
 *   c.config = { type: 'doughnut', groupBy: 'Status', metric: 'count',
 *                colorMap: { Active: 'success', Pending: 'warning', Closed: 'neutral' } };
 *
 * @example  Bubble chart — every record is a point:
 *   c.config = { type: 'bubble', xField: 'revenue', yField: 'employees', rField: 'leadScore',
 *                groupBy: 'Industry' };  // optional groupBy = one dataset per group
 *
 * @example  Radar — multi-axis comparison:
 *   c.config = { type: 'radar', axes: ['Q1', 'Q2', 'Q3', 'Q4'],
 *                groupBy: 'Region', metric: 'sum', field: 'Revenue' };
 *   // → one polygon per Region, with Q1..Q4 axes
 *
 * @example  Mixed — multiple datasets with different types:
 *   c.config = { type: 'mixed', groupBy: 'Month',
 *                series: [
 *                  { type: 'bar',  metric: 'sum',   field: 'Revenue', label: 'Revenue', color: 'primary' },
 *                  { type: 'line', metric: 'count',                    label: 'Orders',  color: 'success' },
 *                ] };
 *
 * Attributes:
 *   label       string — title above chart (optional)
 *   type        chart variant (also via config.type)
 *   height      px (default 240)
 *   color       DaisyUI semantic for monochrome single-series charts
 *
 * Properties:
 *   .records    array
 *   .config     see per-type schemas above
 */
import { groupAggregate } from '../util/aggregate.js';

const DAISY_COLOR_HEX = {
  // Approximate DaisyUI defaults for fallback when oklch() vars aren't resolvable in canvas.
  primary:   '#4f46e5',
  secondary: '#f000b8',
  accent:    '#37cdbe',
  success:   '#16a34a',
  warning:   '#f59e0b',
  error:     '#dc2626',
  info:      '#0ea5e9',
  neutral:   '#737373',
};

const PALETTES = {
  primary:   ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#e0e7ff'],
  rainbow:   ['#4f46e5', '#16a34a', '#f59e0b', '#dc2626', '#0ea5e9', '#f000b8', '#37cdbe', '#737373'],
  sequential:['#0c4a6e', '#075985', '#0369a1', '#0284c7', '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd'],
};

export class GChart extends HTMLElement {
  constructor() {
    super();
    this._records = null;
    this._config = null;
    this._chartInstance = null;
    this._renderScheduled = false;
  }

  static get observedAttributes() {
    return ['label', 'type', 'height', 'color'];
  }

  attributeChangedCallback() { this.scheduleRender(); }

  connectedCallback() {
    // h-full on host + inner card so charts in a multi-column row stay equal
    // height even when one has a longer label or no label at all.
    this.classList.add('block', 'h-full');
    if (!this._root) {
      this._root = document.createElement('div');
      this._root.className = 'card bg-base-100 shadow-sm h-full';
      this.appendChild(this._root);
    }
    this.scheduleRender();
  }

  disconnectedCallback() {
    if (this._chartInstance) {
      this._chartInstance.destroy();
      this._chartInstance = null;
    }
  }

  // ── Public API ──

  get records() { return this._records; }
  set records(rs) { this._records = Array.isArray(rs) ? rs : null; this.scheduleRender(); }

  get config() { return this._config; }
  set config(c) { this._config = c && typeof c === 'object' ? c : null; this.scheduleRender(); }

  // ── Internals ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    const cfg = this._config || {};
    const type = (cfg.type || this.getAttribute('type') || 'bar').toLowerCase();
    const label = this.getAttribute('label') || cfg.displayLabel || '';
    const height = parseInt(this.getAttribute('height'), 10) || 240;

    // Build the card shell (only once if not yet built)
    if (!this._canvas) {
      this._root.innerHTML = `
        <div class="card-body p-4 gap-3">
          ${label ? `<h3 class="font-semibold text-sm text-base-content/80">${escapeHtml(label)}</h3>` : ''}
          <div class="relative" style="height: ${height}px;">
            <canvas></canvas>
          </div>
        </div>
      `;
      this._canvas = this._root.querySelector('canvas');
    } else {
      const heading = this._root.querySelector('h3');
      if (heading && label) heading.textContent = label;
      const wrapper = this._canvas.parentElement;
      if (wrapper) wrapper.style.height = `${height}px`;
    }

    if (typeof window.Chart === 'undefined') {
      console.warn('[g-chart] Chart.js not loaded — include before <g-chart>');
      return;
    }

    const records = Array.isArray(this._records) ? this._records : [];
    if (records.length === 0) {
      this._destroyChart();
      this._renderEmpty();
      return;
    }

    // Auto-promote: series[] is a mixed-chart feature. If a caller specifies
    // series[] on any other chart type (e.g. line or bar), fan it out to
    // mixed semantics — each series inherits the top-level type unless it
    // overrides. Without this, series[] on a line chart is silently dropped
    // by _buildCategorical and only the top-level single series renders.
    const hasSeries = Array.isArray(cfg.series) && cfg.series.length > 0;
    const effectiveType = hasSeries && type !== 'mixed' ? 'mixed' : type;
    const effectiveCfg = hasSeries && type !== 'mixed'
      ? { ...cfg, type: 'mixed', series: cfg.series.map(s => ({ type: type || 'bar', ...s })) }
      : cfg;

    // Dispatch by chart family
    let built;
    if (effectiveType === 'mixed') {
      built = this._buildMixed(records, effectiveCfg, label);
    } else if (effectiveType === 'bubble' || effectiveType === 'scatter') {
      built = this._buildXY(records, effectiveCfg, effectiveType, label);
    } else if (effectiveType === 'radar') {
      built = this._buildRadar(records, effectiveCfg, label);
    } else {
      // categorical: bar, horizontalBar, line, area, pie, doughnut, polarArea
      built = this._buildCategorical(records, effectiveCfg, effectiveType, label);
    }

    if (!built || !built.data) {
      this._destroyChart();
      this._renderEmpty();
      return;
    }

    this._removeEmpty();
    this._destroyChart();
    this._chartInstance = new window.Chart(this._canvas.getContext('2d'), built);
  }

  // ── Builders per chart family ──

  _buildCategorical(records, cfg, type, label) {
    if (!cfg.groupBy || !(cfg.metric || cfg.metric === 'count')) return null;
    const groups = groupAggregate(records, cfg);
    if (!groups.length) return null;
    const labels = groups.map(g => String(g.key));
    const values = groups.map(g => g.value);
    const colors = this._resolveColors(labels, cfg);

    const isHorizontal = type === 'horizontalbar';
    const isCircle = type === 'pie' || type === 'doughnut' || type === 'polararea';
    const isArea = type === 'area';
    const isLine = type === 'line' || isArea;
    const chartType = isHorizontal ? 'bar' : (isArea ? 'line' : (type === 'polararea' ? 'polarArea' : type));

    const dataset = {
      label: cfg.displayLabel || label || 'Value',
      data: values,
      backgroundColor: isCircle ? colors : (isLine ? colorWithAlpha(colors[0], isArea ? 0.3 : 0) : (Array.isArray(colors) ? colors[0] : colors)),
      borderColor: isCircle ? colors : (Array.isArray(colors) ? colors[0] : colors),
      borderWidth: isCircle ? 1 : (isLine ? 2 : 0),
      fill: isArea ? true : undefined,
      tension: isLine ? 0.25 : undefined,
    };

    return {
      type: chartType,
      data: { labels, datasets: [dataset] },
      options: {
        indexAxis: isHorizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: isCircle, position: 'right' },
          tooltip: { callbacks: { label: (item) => formatTooltipValue(item, cfg) } },
        },
        scales: isCircle ? undefined : {
          x: { grid: { display: !isHorizontal } },
          y: { grid: { display: isHorizontal }, beginAtZero: true,
               ticks: { callback: (v) => formatAxisValue(v, cfg) } },
        },
      },
    };
  }

  _buildXY(records, cfg, type, label) {
    // bubble + scatter — every record becomes a point. xField/yField required.
    // rField required for bubble. Optional groupBy creates one dataset per group.
    if (!cfg.xField || !cfg.yField) {
      console.warn('[g-chart] type:' + type + ' requires xField + yField');
      return null;
    }
    const isBubble = type === 'bubble';
    const toPoint = (r) => {
      const x = parseFloat(r[cfg.xField]);
      const y = parseFloat(r[cfg.yField]);
      const point = { x, y };
      if (isBubble) {
        const r0 = parseFloat(r[cfg.rField] ?? cfg.defaultR ?? 6);
        point.r = isNaN(r0) ? 6 : Math.max(3, Math.min(40, r0));
      }
      return point;
    };
    const valid = (p) => !isNaN(p.x) && !isNaN(p.y) && (!isBubble || !isNaN(p.r));

    let datasets;
    if (cfg.groupBy) {
      const groups = new Map();
      for (const r of records) {
        const k = r?.[cfg.groupBy];
        if (k == null || k === '') continue;
        if (!groups.has(k)) groups.set(k, []);
        const p = toPoint(r);
        if (valid(p)) groups.get(k).push(p);
      }
      const keys = Array.from(groups.keys());
      const colors = this._resolveColors(keys.map(String), cfg);
      datasets = keys.map((k, i) => ({
        label: String(k),
        data: groups.get(k),
        backgroundColor: colorWithAlpha(colors[i] || colors[0], 0.6),
        borderColor: colors[i] || colors[0],
      }));
    } else {
      const points = records.map(toPoint).filter(valid);
      const colors = this._resolveColors(['series'], cfg);
      datasets = [{
        label: cfg.displayLabel || label || 'Series',
        data: points,
        backgroundColor: colorWithAlpha(colors[0], 0.6),
        borderColor: colors[0],
      }];
    }

    return {
      type: type, // 'bubble' | 'scatter'
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: !!cfg.groupBy, position: 'right' },
          tooltip: { callbacks: { label: (item) => formatXYTooltip(item, cfg, isBubble) } },
        },
        scales: {
          x: { type: 'linear', position: 'bottom',
               title: { display: true, text: cfg.xLabel || cfg.xField } },
          y: { type: 'linear', beginAtZero: cfg.beginAtZero !== false,
               title: { display: true, text: cfg.yLabel || cfg.yField } },
        },
      },
    };
  }

  _buildRadar(records, cfg, label) {
    // radar: spec.axes = [field1, ...] = the polygon vertices.
    // groupBy + metric = one polygon per group, value per axis.
    if (!Array.isArray(cfg.axes) || cfg.axes.length < 3) {
      console.warn('[g-chart] type:radar requires axes[] of ≥3 fields');
      return null;
    }
    const axes = cfg.axes;
    const labels = axes.slice();

    const computeAxisValue = (groupRecords, field) => {
      const metric = cfg.metric || 'avg';
      if (metric === 'count') return groupRecords.length;
      const vals = groupRecords.map(r => parseFloat(r[field])).filter(v => !isNaN(v));
      if (!vals.length) return 0;
      if (metric === 'sum') return vals.reduce((a, b) => a + b, 0);
      if (metric === 'avg') return vals.reduce((a, b) => a + b, 0) / vals.length;
      if (metric === 'min') return Math.min(...vals);
      if (metric === 'max') return Math.max(...vals);
      return vals.reduce((a, b) => a + b, 0);
    };

    let datasets;
    if (cfg.groupBy) {
      const groups = new Map();
      for (const r of records) {
        const k = r?.[cfg.groupBy];
        if (k == null || k === '') continue;
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
      }
      const keys = Array.from(groups.keys());
      const colors = this._resolveColors(keys.map(String), cfg);
      datasets = keys.map((k, i) => ({
        label: String(k),
        data: axes.map(a => computeAxisValue(groups.get(k), a)),
        backgroundColor: colorWithAlpha(colors[i] || colors[0], 0.25),
        borderColor: colors[i] || colors[0],
        borderWidth: 2,
        pointBackgroundColor: colors[i] || colors[0],
      }));
    } else {
      const colors = this._resolveColors(['series'], cfg);
      datasets = [{
        label: cfg.displayLabel || label || 'Value',
        data: axes.map(a => computeAxisValue(records, a)),
        backgroundColor: colorWithAlpha(colors[0], 0.25),
        borderColor: colors[0],
        borderWidth: 2,
        pointBackgroundColor: colors[0],
      }];
    }

    return {
      type: 'radar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: !!cfg.groupBy, position: 'top' },
          tooltip: { callbacks: { label: (item) => formatTooltipValue(item, cfg) } },
        },
      },
    };
  }

  _buildMixed(records, cfg, label) {
    // series: [{ type, metric, field?, label?, color?, palette? }, ...]
    // All series share spec.groupBy → same labels axis.
    if (!cfg.groupBy || !Array.isArray(cfg.series) || cfg.series.length === 0) {
      console.warn('[g-chart] type:mixed requires groupBy + series[]');
      return null;
    }
    // Compute the union of group keys across all series so labels align.
    // Merge top-level predicate with each series' predicate (AND) so the
    // chart's time-window filter applies to every series uniformly instead
    // of being overridden by the series-specific status filter. Without
    // this merge, `{...cfg, ...s}` would have the series predicate shadow
    // the top-level one — e.g. "last 12 months" would be lost per series.
    const allKeys = new Set();
    const seriesGroups = cfg.series.map(s => {
      const merged = { ...cfg, ...s };
      if (cfg.predicate && s.predicate && cfg.predicate !== s.predicate) {
        merged.predicate = { and: [cfg.predicate, s.predicate] };
      }
      const groups = groupAggregate(records, merged);
      groups.forEach(g => allKeys.add(String(g.key)));
      const map = new Map(groups.map(g => [String(g.key), g.value]));
      return { spec: s, map };
    });
    // Sort the merged label set. Each per-series groupAggregate() call sorts
    // internally, but the union across series needs a second sort pass or
    // time-series with gaps (series A has Jan+Feb, series B has Mar) render
    // out of order. Honors the same sort modes as groupAggregate.
    const labels = Array.from(allKeys);
    const sortMode = cfg.sort || 'value-desc';
    if (sortMode === 'key-asc') labels.sort();
    else if (sortMode === 'key-desc') labels.sort().reverse();
    if (!labels.length) return null;

    const datasets = seriesGroups.map(({ spec, map }, i) => {
      const data = labels.map(l => map.get(l) ?? 0);
      const color = DAISY_COLOR_HEX[spec.color] || PALETTES[spec.palette || 'rainbow'][i % PALETTES.rainbow.length];
      const t = (spec.type || 'bar').toLowerCase();
      const isLine = t === 'line' || t === 'area';
      return {
        type: t === 'area' ? 'line' : t,
        label: spec.label || spec.field || ('Series ' + (i + 1)),
        data,
        backgroundColor: isLine ? colorWithAlpha(color, t === 'area' ? 0.3 : 0) : color,
        borderColor: color,
        borderWidth: isLine ? 2 : 0,
        fill: t === 'area' ? true : undefined,
        tension: isLine ? 0.25 : undefined,
        yAxisID: spec.yAxis || 'y',
      };
    });

    // Detect dual-axis usage
    const usesYRight = datasets.some(d => d.yAxisID === 'yRight');

    return {
      type: 'bar', // base type — Chart.js picks per-dataset from `type` field
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'top' },
          tooltip: { callbacks: { label: (item) => formatTooltipValue(item, cfg) } },
        },
        scales: {
          x: {},
          y: { beginAtZero: true, ticks: { callback: (v) => formatAxisValue(v, cfg) } },
          ...(usesYRight ? { yRight: { position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } } } : {}),
        },
      },
    };
  }

  _destroyChart() {
    if (this._chartInstance) {
      try { this._chartInstance.destroy(); } catch {}
      this._chartInstance = null;
    }
    if (this._canvas && window.Chart) {
      const existing = window.Chart.getChart(this._canvas);
      if (existing) existing.destroy();
    }
  }

  _resolveColors(labels, cfg) {
    if (cfg.colorMap && typeof cfg.colorMap === 'object') {
      return labels.map(l => DAISY_COLOR_HEX[cfg.colorMap[l]] || DAISY_COLOR_HEX.neutral);
    }
    const palette = PALETTES[cfg.palette] || PALETTES.primary;
    const colorAttr = this.getAttribute('color');
    if (colorAttr && DAISY_COLOR_HEX[colorAttr] && labels.length === 1) {
      return [DAISY_COLOR_HEX[colorAttr]];
    }
    if (cfg.type === 'pie' || cfg.type === 'doughnut') {
      // cycle through palette for each segment
      return labels.map((_, i) => palette[i % palette.length]);
    }
    // single-series bar/line — pick the first palette color
    return [palette[0]];
  }

  _renderEmpty() {
    if (this._emptyEl) return;
    const wrapper = this._canvas?.parentElement;
    if (!wrapper) return;
    this._emptyEl = document.createElement('div');
    this._emptyEl.className = 'absolute inset-0 flex items-center justify-center text-base-content/40 text-sm';
    this._emptyEl.textContent = 'No data';
    wrapper.appendChild(this._emptyEl);
  }

  _removeEmpty() {
    if (this._emptyEl) {
      this._emptyEl.remove();
      this._emptyEl = null;
    }
  }
}

function formatTooltipValue(item, cfg) {
  const v = typeof item.parsed === 'object' ? (item.parsed.y ?? item.parsed.x ?? item.parsed) : item.parsed;
  return `${item.label}: ${formatNumber(v, cfg)}`;
}

function formatXYTooltip(item, cfg, isBubble) {
  const p = item.raw;
  const x = formatNumber(p.x, cfg);
  const y = formatNumber(p.y, cfg);
  if (isBubble) return `${item.dataset.label} — ${cfg.xLabel || cfg.xField}: ${x}, ${cfg.yLabel || cfg.yField}: ${y}, r: ${p.r}`;
  return `${item.dataset.label} — (${x}, ${y})`;
}

function colorWithAlpha(hex, alpha) {
  // Accept "#rrggbb" → "rgba(r,g,b,alpha)". If alpha == 0, return transparent.
  if (!hex || typeof hex !== 'string') return alpha === 0 ? 'transparent' : hex;
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const [, r, g, b] = m;
  return `rgba(${parseInt(r, 16)},${parseInt(g, 16)},${parseInt(b, 16)},${alpha})`;
}

function formatAxisValue(v, cfg) {
  return formatNumber(v, cfg);
}

function formatNumber(v, cfg) {
  if (v == null || isNaN(v)) return '';
  if (cfg && cfg.metric === 'percent') return (v * 100).toFixed(1) + '%';
  if (cfg && cfg.currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency, maximumFractionDigits: 0 }).format(v);
    } catch {}
  }
  return new Intl.NumberFormat('en-US').format(v);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-chart')) {
  customElements.define('g-chart', GChart);
}
